import React from 'react'
import { notifications } from '@mantine/notifications'
import { useDantiAuth } from '@/hooks/use-danti-auth'
import {
  type DeleteHistoryParams,
  type HistoryItem,
  useDeleteHistoryMutation,
  usePageHistory,
  useRecentHistory,
} from '@/utils/data-fetching/api/history'
import { singletonHook } from 'react-singleton-hook'

export { type HistoryItem } from '@/utils/data-fetching/api/history'

export interface UseHistoryHookResponse {
  recentItems: HistoryItem[]
  totalCount: number
  pagedItems: HistoryItem[]
  isLoading: boolean
  currentPage: number
  pageSize: number
  nextPage: () => void
  previousPage: () => void
  deleteHistory: (params: DeleteHistoryParams) => void
  search: (term: string) => void
  searchTerm: string
}

const useHistoryHookImpl = (): UseHistoryHookResponse => {
  const { isAuthenticated } = useDantiAuth()

  const [currentPage, setCurrentPage] = React.useState(0)
  const [searchTerm, setSearchTerm] = React.useState('')

  const pageSize = 50

  const { data: recentHistory } = useRecentHistory({ isAuthenticated })

  const recentItems = React.useMemo(
    () => recentHistory?.items ?? [],
    [recentHistory],
  )

  const { data: pageHistory, isLoading } = usePageHistory({
    currentPage,
    searchTerm,
    isAuthenticated,
  })

  const pagedItems = React.useMemo(
    () => pageHistory?.items ?? [],
    [pageHistory?.items],
  )

  const totalCount = React.useMemo(() => pageHistory?.total ?? 0, [pageHistory])

  const maxPages = React.useMemo(
    () => Math.ceil(totalCount / pageSize),
    [totalCount],
  )

  const [deleteParams, setDeleteParams] = React.useState<DeleteHistoryParams>()

  const deleteHistoryMutation = useDeleteHistoryMutation()

  function deleteHistory(params: DeleteHistoryParams) {
    deleteHistoryMutation.mutate(params)
    setDeleteParams(params)
  }

  React.useEffect(() => {
    const { status } = deleteHistoryMutation
    const isDeleteAll = deleteParams?.all ?? false
    const deleteItemLength = (deleteParams?.ids ?? []).length ?? 0

    if (status === 'success' && (isDeleteAll || deleteItemLength > 0)) {
      let message = ''

      if (isDeleteAll) {
        message = 'All History removed'
      } else if (deleteItemLength === 1) {
        message = 'Item removed'
      } else {
        message = 'Items removed'
      }

      notifications.clean()
      notifications.show({
        title: 'Success',
        color: 'green',
        message,
        autoClose: 5000,
        withCloseButton: true,
      })
    } else if (status === 'error') {
      notifications.clean()
      notifications.show({
        title: 'Error',
        color: 'red',
        message: 'Error removing history',
        autoClose: 5000,
        withCloseButton: true,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteHistoryMutation.status])

  const nextPage = () => {
    setCurrentPage((page) => Math.min(maxPages, page + 1))
  }

  const previousPage = () => {
    setCurrentPage((page) => Math.max(0, page - 1))
  }

  const search = React.useCallback((term: string) => {
    setCurrentPage(0)
    setSearchTerm(term)
  }, [])

  return {
    recentItems,
    totalCount,
    pagedItems,
    isLoading,
    deleteHistory,
    pageSize,
    currentPage,
    nextPage,
    previousPage,
    search,
    searchTerm,
  }
}

export const useHistory = singletonHook<UseHistoryHookResponse>(
  () => ({
    recentItems: [],
    totalCount: 0,
    pagedItems: [],
    isLoading: false,
    deleteHistory: () => {},
    pageSize: 0,
    currentPage: 0,
    nextPage: () => {},
    previousPage: () => {},
    search: () => {},
    searchTerm: '',
  }),
  useHistoryHookImpl,
)
